import { useCallback, useMemo } from 'react';

import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';

import { usersRolesEndpoints as endpoints, membersEndpoints } from '@/api/endpoints';
import { callGet, callPost, callDelete } from '@/api/fetcher';
import { Department, Departments } from '@/modules/departments/models';
import { selectAccountId, selectAccountSlug } from '@/redux/session/selectors';
import { Members } from '@/shared/types/member';
import { Role, Roles, PartialRole } from '@/shared/types/role';
interface UpdateProps {
  user_id: string;
  role_ids: string[];
}

interface RemoveProps {
  user_id: string;
  role_id: string;
}

const EMPTY_DEPARTMENTS: readonly Department[] = Object.freeze([]);
const EMPTY_ROLES: readonly Role[] = Object.freeze([]);

const API = {
  listRoles: async (user_id: string): Promise<Roles> => callGet(endpoints.roles(user_id)),
  assignRoles: async ({ user_id, role_ids }: UpdateProps): Promise<Roles> =>
    callPost(endpoints.roles(user_id), { role_ids }),
  removeRole: async ({ user_id, role_id }: RemoveProps): Promise<PartialRole> =>
    callDelete(endpoints.role(user_id, role_id)),
  userDepartments: async (id: string): Promise<Departments> =>
    callGet(endpoints.userDepartments(id)),
};

export const onRoleAssigned = (
  queryClient: QueryClient,
  resp: Roles,
  user_id: string,
  accountSlug: string
) => {
  queryClient.setQueryData<Members>([membersEndpoints.members, accountSlug], (prev: Members) => {
    const members = prev.members.map((m) => {
      if (m.user_id === user_id) {
        return { ...m, role_ids: [...m.role_ids, ...resp.roles.map((r) => r.role_id)] };
      }
      return m;
    });
    return { members };
  });
};

export const onRoleRemoved = (
  queryClient: QueryClient,
  resp: PartialRole,
  user_id: string,
  accountSlug: string
) => {
  queryClient.setQueryData<Members>([membersEndpoints.members, accountSlug], (prev: Members) => {
    const members = prev.members.map((m) => {
      if (m.user_id === user_id) {
        return { ...m, role_ids: m.role_ids.filter((r) => r !== resp.role_id) };
      }
      return m;
    });
    return { members };
  });
};

export const useUserRoles = (user_id: string) => {
  const accountId = useSelector(selectAccountId);
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const accountSlug = useSelector(selectAccountSlug);

  const { data: roles, status: listStatus } = useQuery<Roles, Error>({
    queryKey: [endpoints.roles(user_id), accountId],
    queryFn: () => API.listRoles(user_id),
    enabled: !!accountId && !!user_id,
  });

  const { data: userDepartments } = useQuery<Departments>({
    queryKey: [endpoints.userDepartments(user_id)],
    queryFn: () => API.userDepartments(user_id),
    enabled: !!user_id,
  });

  const { mutateAsync: assignRoles, status: updateStatus } = useMutation<Roles, Error, UpdateProps>(
    {
      mutationFn: API.assignRoles,
      onSuccess: (resp, variables) => {
        onRoleAssigned(queryClient, resp, variables.user_id, accountSlug);
        toast.success(t('roles.role_assigned'));
      },
      meta: { errorToast: true },
    }
  );

  const { mutateAsync: removeRole, status: removeStatus } = useMutation<
    PartialRole,
    Error,
    RemoveProps
  >({
    mutationFn: API.removeRole,
    onSuccess: (resp, variables) => {
      onRoleRemoved(queryClient, resp, variables.user_id, accountSlug);
      toast.success(t('roles.role_removed'));
    },
    meta: { errorToast: true },
  });

  // Grouped once; filtering per call returned a fresh array and broke every caller's memo.
  // Undefined until they load, so callers can tell "none" from "not loaded yet".
  const departmentsByDesk = useMemo(() => {
    if (!userDepartments?.departments) {
      return null;
    }
    const byDesk = new Map<string, Department[]>();
    userDepartments.departments.forEach((department) => {
      const existing = byDesk.get(department.desk_id);
      if (existing) {
        existing.push(department);
      } else {
        byDesk.set(department.desk_id, [department]);
      }
    });
    return byDesk;
  }, [userDepartments?.departments]);

  const getUserDepartments = useCallback(
    (desk_id: string): readonly Department[] | undefined =>
      departmentsByDesk ? (departmentsByDesk.get(desk_id) ?? EMPTY_DEPARTMENTS) : undefined,
    [departmentsByDesk]
  );

  return {
    roles: roles?.roles ?? EMPTY_ROLES,
    getUserDepartments,
    listStatus,
    assignRoles,
    removeRole,
    updateStatus,
    removeStatus,
  };
};
